import { Slice } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import isURL from 'validator/lib/isURL';

import { EditorView } from '@lyearn/lygo-core';

import { linkMarkMenuKey } from '../../../plugins/LinkMarkMenu';
import { getElementPos, isInCode, selectionType, setSelection } from '../../../utils';
import { LINK_ELEMENT_CLASS } from '../../Link';

function plugins() {
  return [
    new Plugin({
      props: {
        handleDOMEvents: {
          click: (editorView: EditorView, event: MouseEvent) => {
            if (!editorView.editable) {
              return false;
            }
            // we can have <a> element in web-bookmark block also.
            const linkElement = event
              .composedPath()
              .find(
                (element) =>
                  (element as HTMLLinkElement).localName === 'a' &&
                  (element as HTMLLinkElement).classList.contains(LINK_ELEMENT_CLASS),
              ) as HTMLLinkElement;

            if (!linkElement) {
              return false;
            }
            const elementPos = getElementPos(linkElement, editorView);
            const href = linkElement.getAttribute('href');
            const length = linkElement.textContent?.length;

            if ((event.metaKey || event.ctrlKey) && href) {
              window.open(href, '_blank');
              const pos = editorView.posAtCoords({ left: event.clientX, top: event.clientY });
              if (pos) {
                setSelection(editorView, selectionType.TEXT, pos.pos);
              }
            } else if (href && elementPos && length) {
              setSelection(editorView, selectionType.TEXT, elementPos.pos, elementPos.pos + length);

              editorView.dispatch(
                editorView.state.tr.setMeta(linkMarkMenuKey, {
                  open: true,
                  link: href,
                }),
              );
            }

            return false;
          },
        },
        handlePaste: (view: EditorView, _event: ClipboardEvent, slice: Slice) => {
          const { state } = view;
          const { selection } = state;
          const { empty, ranges } = selection;
          const linkMark = view.state.schema.marks.link;
          const textContent = (slice.content as any).textBetween(0, slice.content.size).trim();

          if (!textContent || !isURL(textContent) || isInCode(state)) {
            return false;
          }

          if (empty) {
            view.dispatch(
              view.state.tr.replaceSelectionWith(
                view.state.schema.text(textContent, [linkMark.create({ href: textContent })]),
                false,
              ),
            );
          } else {
            ranges.forEach((range) => {
              const from = range.$from.pos;
              const to = range.$to.pos;

              state.doc.nodesBetween(from, to, (node, pos) => {
                const trimmedFrom = Math.max(pos, from);
                const trimmedTo = Math.min(pos + node.nodeSize, to);
                view.dispatch(
                  view.state.tr.addMark(
                    trimmedFrom,
                    trimmedTo,
                    linkMark.create({ href: textContent }),
                  ),
                );
              });
            });
          }

          return true;
        },
      },
    }),
  ];
}

export default plugins;
